import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  Building2,
  DollarSign,
  TrendingUp,
  Clock,
  FileText,
} from "lucide-react";
import apiService from "../services/api";
import { useAuth } from "../context/AuthContext";
import "../styles/Dashboard.css";

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalEmployees: 0,
    totalCustomers: 0,
    activeEmployees: 0,
    monthlySalaries: 0,
  });
  const [presenceStats, setPresenceStats] = useState({
    totalHours: 0,
    totalRegularHours: 0,
    totalWeekendHours: 0,
    kablanHours: 0,
    companyHours: 0,
    month: "",
  });
  const [topCustomers, setTopCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
    loadPresenceStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      // Load employees
      const employeesResponse = await apiService.getEmployees({ limit: 1500 });
      const employees = employeesResponse.employees || [];

      // Load customers (sites)
      const customersResponse = await apiService.getCustomers({ limit: 1500 });
      const customers = customersResponse.customers || [];

      // Count unique customer names (API returns 'name' field)
      const uniqueCustomers = new Set(customers.map((c) => c.name)).size;

      // Calculate active employees
      const activeEmployees = employees.filter((emp) => emp.active).length;

      // Load salaries for last month
      const now = new Date();
      const lastMonth = `${now.getFullYear()}-${String(now.getMonth()).padStart(
        2,
        "0",
      )}`;
      const salariesResponse = await apiService.getSalarySummary("2025-09");
      const monthlySalaries = salariesResponse.total_to_pay || 0;

      setStats({
        totalEmployees: employees.length,
        totalCustomers: uniqueCustomers,
        activeEmployees: activeEmployees,
        monthlySalaries: monthlySalaries,
      });
    } catch (error) {
      console.error("Failed to load stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadPresenceStats = async () => {
    try {
      // Get last month
      const now = new Date();
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthStr = `${lastMonth.getFullYear()}-${String(
        lastMonth.getMonth() + 1,
      ).padStart(2, "0")}`;

      // Load presence data for last month
      const presenceResponse = await apiService.getPresence(lastMonthStr);

      if (presenceResponse.success) {
        const data = presenceResponse.data || [];

        // Calculate total hours
        const totalRegular = data.reduce(
          (sum, item) => sum + (item.total_regular_hours || 0),
          0,
        );
        const totalWeekend = data.reduce(
          (sum, item) => sum + (item.total_weekend_hours || 0),
          0,
        );
        const totalHours = totalRegular + totalWeekend;

        // Calculate hours by employee type
        const kablanData = data.filter((item) => item.employee_type === "קבלן");
        const kablanHours = kablanData.reduce(
          (sum, item) =>
            sum +
            (item.total_regular_hours || 0) +
            (item.total_weekend_hours || 0),
          0,
        );

        const companyData = data.filter(
          (item) =>
            item.employee_type === "חברה" ||
            item.employee_type === "קבלן ו חברה",
        );
        const companyHours = companyData.reduce(
          (sum, item) =>
            sum +
            (item.total_regular_hours || 0) +
            (item.total_weekend_hours || 0),
          0,
        );

        setPresenceStats({
          totalHours,
          totalRegularHours: totalRegular,
          totalWeekendHours: totalWeekend,
          kablanHours,
          companyHours,
          month: lastMonthStr,
        });

        // Calculate top customers by total hours
        const customerHours = {};
        data.forEach((item) => {
          const customerName = item.customer_name || "ללא לקוח";
          const itemTotalHours =
            (item.total_regular_hours || 0) + (item.total_weekend_hours || 0);

          if (customerHours[customerName]) {
            customerHours[customerName] += itemTotalHours;
          } else {
            customerHours[customerName] = itemTotalHours;
          }
        });

        // Convert to array and sort by hours
        const sortedCustomers = Object.entries(customerHours)
          .map(([name, hours]) => ({ name, hours }))
          .sort((a, b) => b.hours - a.hours)
          .slice(0, 4); // Get top 4

        setTopCustomers(sortedCustomers);
      }
    } catch (error) {
      console.error("Failed to load presence stats:", error);
    }
  };

  // Build stat cards based on user permission
  const statCards = [
    {
      title: 'סה"כ עובדים',
      value: stats.totalEmployees,
      icon: Users,
      color: "#3b82f6",
      link: "/employees",
      description: "כל העובדים הרשומים במערכת",
    },
    {
      title: "עובדים פעילים",
      value: stats.activeEmployees,
      icon: TrendingUp,
      color: "#10b981",
      link: "/employees",
      description: "עובדים פעילים כרגע",
    },
    {
      title: 'סה"כ לקוחות',
      value: stats.totalCustomers,
      icon: Building2,
      color: "#8b5cf6",
      link: "/customers",
      description: "לקוחות וחברות פעילות",
    },
  ];

  // Only show salary stats for site_admin, admin, and owner
  // if (user?.permission !== "viewer") {
  //   statCards.push({
  //     title: "משכורות חודשיות",
  //     value: `₪${stats.monthlySalaries.toLocaleString()}`,
  //     icon: DollarSign,
  //     color: "#f59e0b",
  //     link: "/salaries",
  //   });
  // }

  const quickActions = [
    {
      title: "העלאת קבצים",
      description: "העלה קבצי T&M או חברות",
      icon: FileText,
      link: "/upload",
      color: "#3b82f6",
    },
    {
      title: "ניהול עובדים",
      description: "צפייה ועריכת רשומות עובדים",
      icon: Users,
      link: "/employees",
      color: "#10b981",
    },
    {
      title: "יצירת משכורות",
      description: "חישוב משכורות חודשיות",
      icon: DollarSign,
      link: "/salaries/generate",
      color: "#f59e0b",
    },
  ];

  return (
    <div className='dashboard'>
      <div className='dashboard-header'>
        <div>
          <h1>לוח בקרה</h1>
          <p>ברוכים הבאים למערכת ניהול עובדים קורל</p>
        </div>
        <div className='dashboard-time'>
          <Clock size={18} />
          <span>
            {new Date().toLocaleDateString("he-IL", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </span>
        </div>
      </div>

      {loading ? (
        <div className='loading-container'>
          <div className='spinner'></div>
          <p>טוען נתונים סטטיסטיים...</p>
        </div>
      ) : (
        <>
          <div className='stats-grid'>
            {statCards.map((stat, index) => (
              <Link to={stat.link} key={index} className='stat-card'>
                <div
                  className='stat-icon'
                  style={{ background: `${stat.color}20`, color: stat.color }}
                >
                  <stat.icon size={24} />
                </div>
                <div className='stat-content'>
                  <p className='stat-title'>{stat.title}</p>
                  <h2 className='stat-value'>{stat.value}</h2>
                  {stat.description && (
                    <p className='stat-description'>{stat.description}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>

          <div className='presence-section'>
            <div className='section-header'>
              <h2>נוכחות - חודש שעבר</h2>
              <Link to='/presence' className='view-all-link'>
                צפה בכל הנתונים →
              </Link>
            </div>
            <div className='presence-stats-grid'>
              <div className='presence-stat-card total'>
                <div className='presence-stat-icon'>
                  <Clock size={32} />
                </div>
                <div className='presence-stat-content'>
                  <p className='presence-stat-label'>סה״כ שעות החודש</p>
                  <h3 className='presence-stat-value'>
                    {presenceStats.totalHours.toLocaleString("he-IL", {
                      maximumFractionDigits: 0,
                    })}
                  </h3>
                  <div className='presence-stat-breakdown'>
                    <span>
                      רגילות:{" "}
                      {presenceStats.totalRegularHours.toLocaleString("he-IL", {
                        maximumFractionDigits: 0,
                      })}
                    </span>
                    <span className='divider'>•</span>
                    <span>
                      סופ״ש:{" "}
                      {presenceStats.totalWeekendHours.toLocaleString("he-IL", {
                        maximumFractionDigits: 0,
                      })}
                    </span>
                  </div>
                </div>
              </div>

              <div className='presence-stat-card kablan'>
                <div className='presence-stat-icon'>
                  <Users size={32} />
                </div>
                <div className='presence-stat-content'>
                  <p className='presence-stat-label'>שעות קבלנים</p>
                  <h3 className='presence-stat-value'>
                    {presenceStats.kablanHours.toLocaleString("he-IL", {
                      maximumFractionDigits: 0,
                    })}
                  </h3>
                  <div className='presence-stat-percentage'>
                    {presenceStats.totalHours > 0
                      ? `${(
                          (presenceStats.kablanHours /
                            presenceStats.totalHours) *
                          100
                        ).toFixed(1)}% מסך השעות`
                      : "0%"}
                  </div>
                </div>
              </div>

              <div className='presence-stat-card company'>
                <div className='presence-stat-icon'>
                  <Building2 size={32} />
                </div>
                <div className='presence-stat-content'>
                  <p className='presence-stat-label'>שעות חברה</p>
                  <h3 className='presence-stat-value'>
                    {presenceStats.companyHours.toLocaleString("he-IL", {
                      maximumFractionDigits: 0,
                    })}
                  </h3>
                  <div className='presence-stat-percentage'>
                    {presenceStats.totalHours > 0
                      ? `${(
                          (presenceStats.companyHours /
                            presenceStats.totalHours) *
                          100
                        ).toFixed(1)}% מסך השעות`
                      : "0%"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='top-customers-section'>
            <h2>לקוחות מובילים - חודש שעבר</h2>
            <div className='top-customers-grid'>
              {topCustomers.length > 0 ? (
                topCustomers.map((customer, index) => (
                  <div key={index} className='customer-card'>
                    <div className='customer-rank'>#{index + 1}</div>
                    <div className='customer-info'>
                      <h3 className='customer-name'>{customer.name}</h3>
                      <div className='customer-hours'>
                        <Clock size={18} />
                        <span className='hours-value'>
                          {customer.hours.toLocaleString("he-IL", {
                            maximumFractionDigits: 0,
                          })}{" "}
                          שעות
                        </span>
                      </div>
                      {presenceStats.totalHours > 0 && (
                        <div className='customer-percentage'>
                          {(
                            (customer.hours / presenceStats.totalHours) *
                            100
                          ).toFixed(1)}
                          % מסך השעות
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className='no-data'>אין נתונים זמינים</div>
              )}
            </div>
          </div>

          <div className='quick-actions'>
            <h2>פעולות מהירות</h2>
            <div className='actions-grid'>
              {quickActions.map((action, index) => (
                <Link to={action.link} key={index} className='action-card'>
                  <div
                    className='action-icon'
                    style={{
                      background: `${action.color}20`,
                      color: action.color,
                    }}
                  >
                    <action.icon size={28} />
                  </div>
                  <h3>{action.title}</h3>
                  <p>{action.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
